import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiStar, HiPlus, HiCheck, HiInformationCircle } from 'react-icons/hi';
import { watchlistAPI } from '../services/api';
import MediaModal from './MediaModal';
import toast from 'react-hot-toast';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w500';

function MediaCard({ item, onUpdate }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistItemId, setWatchlistItemId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addingSeries, setAddingSeries] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const mediaType = item.media_type || (item.first_air_date ? 'tv' : 'movie');
  const title = item.title || item.name;
  const releaseDate = item.release_date || item.first_air_date;
  const year = releaseDate ? new Date(releaseDate).getFullYear() : '';

  useEffect(() => {
    checkWatchlist();
  }, [item.id]);

  const checkWatchlist = async () => {
    try {
      const response = await watchlistAPI.check(item.id, mediaType);
      setInWatchlist(response.data.in_watchlist);
      setWatchlistItemId(response.data.item_id);
    } catch (error) {
      console.error('Error checking watchlist:', error);
    }
  };

  const handleAddToWatchlist = async (e) => {
    e.stopPropagation();
    if (loading) return;

    setLoading(true);
    if (!inWatchlist && mediaType === 'tv') setAddingSeries(true);
    try {
      if (inWatchlist) {
        await watchlistAPI.remove(watchlistItemId);
        setInWatchlist(false);
        setWatchlistItemId(null);
        toast.success(t('media.removedFromList'));
      } else {
        const response = await watchlistAPI.add({
          tmdb_id: item.id,
          media_type: mediaType,
          title: title,
          poster_path: item.poster_path,
          overview: item.overview,
          release_date: releaseDate,
          vote_average: item.vote_average,
        });
        setInWatchlist(true);
        setWatchlistItemId(response.data.id);
        toast.success(t('media.addedToList'));
      }
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('media.errorUpdatingList'));
    } finally {
      setLoading(false);
      setAddingSeries(false);
    }
  };

  const handleClick = () => {
    if (mediaType === 'tv' && inWatchlist) {
      navigate(`/series/${watchlistItemId}`);
    }
  };

  const handleMoreInfo = (e) => {
    e.stopPropagation();
    setShowModal(true);
  };

  return (
    <>
      <div className="media-card" onClick={handleClick}>
        <div className="media-card-image">
          {item.poster_path ? (
            <img
              src={`${TMDB_IMAGE_BASE}${item.poster_path}`}
              alt={title}
              loading="lazy"
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#e5e7eb',
                color: '#9ca3af',
                fontSize: '14px',
                textAlign: 'center',
                padding: '20px',
              }}
            >
              {t('media.noImage')}
            </div>
          )}
          <span className={`media-card-badge ${mediaType}`}>
            {mediaType === 'movie' ? t('navigation.movies').slice(0, -1) : t('navigation.series').slice(0, -1)}
          </span>
          {item.vote_average > 0 && (
            <span className="media-card-rating">
              <HiStar />
              {item.vote_average.toFixed(1)}
            </span>
          )}
          <div className="media-card-overlay">
            <div className="media-card-actions">
              <button
                className={inWatchlist ? 'btn-added' : 'btn-add'}
                onClick={handleAddToWatchlist}
                disabled={loading}
              >
                {inWatchlist ? (
                  <>
                    <HiCheck /> {t('media.inList')}
                  </>
                ) : (
                  <>
                    <HiPlus /> {t('common.add')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="media-card-content">
            <div>
              <h3 className="media-card-title">{title}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <p className="media-card-year">{year}</p>
                <button
                  className="btn-more-info"
                  onClick={handleMoreInfo}
                >
                  <HiInformationCircle />
                </button>
              </div>
            </div>
        </div>
      </div>

      {showModal && createPortal(
        <MediaModal item={item} onClose={() => setShowModal(false)} />,
        document.body
      )}

      {addingSeries && createPortal(
        <div className="adding-series-overlay">
          <div className="adding-series-overlay__content">
            <h3 className="adding-series-overlay__title">{t('media.addingSeries')}</h3>
            <p className="adding-series-overlay__subtitle">{t('media.loadingEpisodes')}</p>
            <div className="adding-series-overlay__progress-bar">
              <div className="adding-series-overlay__progress-fill" />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default MediaCard;
